import { useState } from "react";
import { fmt } from "../utils/fmt";
import { DEFAULT_STATS, fmtDuration } from "../utils/save";
import { SHIP_PARTS, RARITY_COLOR, RARITY_LABEL } from "../data/shipParts";

// ── Formatters ────────────────────────────────────────────────────────────────
const pct    = v => `${Math.round((v - 1) * 100)}%`;
const add    = (v, u = "") => v >= 0 ? `+${Math.round(v)}${u}` : `${Math.round(v)}${u}`;
const bool   = v => v ? "YES" : "NO";
const frames = v => `${Math.round(v)} fr`;

const SECTIONS = [
  {
    label: "MINING",
    color: "#00ccff",
    rows: [
      { key: "laserDamage",   label: "Laser Damage",    fmt: v => fmt(v) },
      { key: "laserRange",    label: "Laser Range",     fmt: v => fmt(v) + "px" },
      { key: "beamWidth",     label: "Beam Width",      fmt: v => `×${v.toFixed(2)}` },
      { key: "rotSpeed",      label: "Rotation Speed",  fmt: v => v.toFixed(3) },
      { key: "attractRange",  label: "Attract Range",   fmt: v => fmt(v) + "px" },
      { key: "bonusLoot",     label: "Bonus Loot",      fmt: v => add(v) },
      { key: "autoBreak",     label: "Auto-Break",      fmt: v => v > 0 ? `${Math.round(v * 100)}%` : "OFF" },
      { key: "spawnRate",     label: "Spawn Rate",      fmt: v => pct(v) },
      { key: "debrisCap",     label: "Debris Cap",      fmt: v => fmt(v) },
      { key: "droneMax",      label: "Drones",          fmt: v => fmt(v) },
    ],
  },
  {
    label: "SMELTING",
    color: "#ff9944",
    rows: [
      { key: "smeltSpeed",    label: "Smelt Speed",     fmt: v => pct(v) },
      { key: "smeltYield",    label: "Smelt Yield",     fmt: v => pct(v) },
      { key: "smeltCostMult", label: "Input Cost",      fmt: v => `×${v.toFixed(2)}` },
      { key: "smeltQueueMax", label: "Queue Slots",     fmt: v => fmt(v) },
    ],
  },
  {
    label: "COMBAT",
    color: "#ff4455",
    rows: [
      { key: "playerMaxHp",      label: "Max HP",           fmt: v => fmt(v) },
      { key: "shieldMax",        label: "Shield",           fmt: v => v > 0 ? fmt(v) : "NONE" },
      { key: "shipSpeed",        label: "Ship Speed",       fmt: v => v.toFixed(2) },
      { key: "blasterDamage",    label: "Blaster Damage",   fmt: v => fmt(v) },
      { key: "blasterFireRate",  label: "Fire Rate",        fmt: frames },
      { key: "blasterCount",     label: "Shots / Fire",     fmt: v => fmt(v) },
      { key: "coreHeatMax",      label: "Heat Cap",         fmt: v => fmt(v) },
      { key: "coreHeatPerShot",  label: "Heat / Shot",      fmt: v => v.toFixed(2) },
      { key: "coreHeatCoolRate", label: "Heat Cooldown",    fmt: v => `${v.toFixed(2)}/fr` },
      { key: "qteSliderWidth",   label: "QTE Green Zone",   fmt: v => `${Math.round(v * 100)}%` },
      { key: "qteFrigateMitigation", label: "Damage Mitigation", fmt: v => v > 0 ? `${Math.round(v * 100)}%` : "OFF" },
      { key: "qteBreachSlow",    label: "Breach Slow",      fmt: v => v > 0 ? `${Math.round(v * 100)}%` : "OFF" },
    ],
  },
  {
    label: "RIFT ECHOES",
    color: "#bb77ff",
    rows: [
      { key: "activeCardSlots",  label: "Echo Slots",       fmt: v => fmt(v) },
      { key: "echoFrequency",    label: "Echo Frequency",   fmt: v => add(Math.round(v * 100), "%") },
      { key: "echoCurrencyBonus",label: "Echo Currency+",   fmt: v => add(v) },
      { key: "echoCardBoost",    label: "Echo Boost+",      fmt: v => add(Math.round(v * 100), "%") },
    ],
  },
  {
    label: "XP",
    color: "#ffcc44",
    rows: [
      { key: "xpMult",        label: "XP Multiplier",  fmt: v => pct(v) },
      { key: "xpPerMine",     label: "XP / Mine",      fmt: v => add(v) },
      { key: "xpPerKill",     label: "XP / Kill",      fmt: v => add(v) },
      { key: "xpLevelUpHeal", label: "Level-Up Heal",  fmt: v => v > 0 ? `+${v} HP` : "—" },
      { key: "xpLevelUpFuel", label: "Level-Up Fuel",  fmt: v => v > 0 ? `+${v}` : "—" },
    ],
  },
  {
    label: "TRADE",
    color: "#44ffcc",
    rows: [
      { key: "tradeCooldownHours", label: "Shop Reset",   fmt: v => v === 0 ? "INSTANT" : `${v}h` },
      { key: "tradeBuyMult",       label: "Buy Prices",   fmt: v => `×${v.toFixed(2)}` },
      { key: "tradeSellBonus",     label: "Sell Bonus",   fmt: v => add(Math.round(v * 100), "%") },
      { key: "tradeStockMult",     label: "Shop Stock",   fmt: v => `×${v.toFixed(2)}` },
    ],
  },
  {
    label: "STARMAP",
    color: "#33dd99",
    rows: [
      { key: "fastTravelFree",  label: "Free Fast-Travel", fmt: bool },
      { key: "scannerCharges",  label: "Scanner Charges",  fmt: v => v > 0 ? fmt(v) : "NONE" },
      { key: "scannerReveal",   label: "Scanner Reveal",   fmt: v => v > 0 ? fmt(v) : "—" },
      { key: "sectorDiversity", label: "Sector Diversity", fmt: v => fmt(v) },
    ],
  },
];

// ── Source tags ───────────────────────────────────────────────────────────────
// Returns an array of { label, color } chips for where a stat's bonus comes from.
function getSourceTags(key, effectiveStats, baseStats, shipPartBonuses, cardStatBonuses) {
  const tags = [];
  const base    = DEFAULT_STATS[key];
  const current = effectiveStats[key];
  if (base == null || current == null) return tags;

  // Ship part contribution
  const partDelta = shipPartBonuses?.[key];
  if (partDelta != null && partDelta !== 0 && partDelta !== false) {
    tags.push({ label: "SHIP", color: "#ff9944" });
  }

  // Card contribution
  const cardDelta = cardStatBonuses?.[key];
  if (cardDelta != null && cardDelta !== 0) {
    tags.push({ label: "ECHO", color: "#bb77ff" });
  }

  // Research contribution: if effective > base + part + card deltas, research is involved
  // (Simplified: if base stats differ from DEFAULT_STATS for this key, research is active)
  if (baseStats && baseStats[key] != null && baseStats[key] !== DEFAULT_STATS[key]) {
    tags.push({ label: "RESEARCH", color: "#44aaff" });
  }

  return tags;
}

// ── Stat row ──────────────────────────────────────────────────────────────────
function StatRow({ row, value, sectionColor, isDefault, isLast, sourceTags }) {
  const hasTags = sourceTags.length > 0;

  return (
    <div
      onClick={() => {}}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "6px 10px",
        borderBottom: !isLast ? "1px solid rgba(255,255,255,0.04)" : "none",
        cursor: hasTags ? "pointer" : "default",
        position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>{row.label}</span>
        {hasTags && sourceTags.map(t => (
          <span key={t.label} style={{
            fontSize: 6, fontWeight: 800, letterSpacing: 0.8,
            color: t.color, border: `1px solid ${t.color}55`,
            borderRadius: 3, padding: "1px 3px", lineHeight: 1.4,
          }}>{t.label}</span>
        ))}
      </div>
      <span style={{
        fontSize: 12, fontWeight: 700,
        color: isDefault ? "rgba(255,255,255,0.3)" : sectionColor,
      }}>{value}</span>
    </div>
  );
}

// ── Ship parts section ────────────────────────────────────────────────────────
function ShipPartsSection({ ownedTiers, equippedTiers }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 5,
          fontFamily: "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: "#ff9944cc" }}>
            SHIP PARTS
          </span>
          <span style={{ fontSize: 8, color: "rgba(255,255,255,0.25)" }}>
            {SHIP_PARTS.filter(p => (equippedTiers[p.id] ?? 0) > 0).length} upgraded
          </span>
        </div>
        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)" }}>{expanded ? "▲" : "▼"}</span>
      </button>

      <div style={{
        background: "rgba(255,255,255,0.02)", borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden",
      }}>
        {SHIP_PARTS.map((part, i) => {
          const equippedIdx = equippedTiers[part.id] ?? 0;
          const ownedMax    = ownedTiers[part.id]    ?? 0;
          const tier        = part.tiers[equippedIdx];
          const rc          = RARITY_COLOR[tier.rarity];
          const upgraded    = equippedIdx > 0;
          const hasBetter   = ownedMax > equippedIdx;
          const effects     = tier.effects ?? {};
          const effectKeys  = Object.keys(effects).filter(k => effects[k] !== 0 && effects[k] !== false);

          return (
            <div key={part.id} style={{
              padding: "7px 10px",
              borderBottom: i < SHIP_PARTS.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{part.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontSize: 10, fontWeight: 700,
                      color: upgraded ? rc : "rgba(255,255,255,0.4)" }}>
                      {tier.name}
                    </span>
                    <span style={{ fontSize: 7, fontWeight: 800, letterSpacing: 0.8,
                      color: rc, background: `${rc}18`, padding: "1px 3px", borderRadius: 3 }}>
                      {RARITY_LABEL[tier.rarity]}
                    </span>
                    {hasBetter && (
                      <span style={{ fontSize: 7, color: "#44ffaa" }}>↑ BETTER OWNED</span>
                    )}
                  </div>
                  <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>{part.name} · {part.slot}</span>
                </div>

                {/* Compact effects chips */}
                {upgraded && expanded && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "flex-end", maxWidth: 130 }}>
                    {effectKeys.map(k => {
                      const statDef = part.stats.find(s => s.key === k);
                      const val = effects[k];
                      if (!statDef) return null;
                      const sign = typeof val === "boolean" ? "" : val > 0 ? "+" : "";
                      const disp = typeof val === "boolean" ? (val ? "YES" : "NO") : statDef.fmt(val);
                      return (
                        <span key={k} style={{
                          fontSize: 8, color: val > 0 || val === true ? "#44ffaa" : "#ff8866",
                          background: "rgba(255,255,255,0.04)", borderRadius: 3, padding: "1px 4px",
                        }}>{sign}{disp}</span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Playtime section ─────────────────────────────────────────────────────────
function PlaytimeSection({ onlinePlaytime, offlinePlaytime }) {
  const online  = onlinePlaytime  ?? 0;
  const offline = offlinePlaytime ?? 0;
  const total   = online + offline;
  const onlinePct  = total > 0 ? Math.round((online  / total) * 100) : 0;
  const offlinePct = total > 0 ? 100 - onlinePct : 0;

  return (
    <div style={{
      marginBottom: 14, padding: "10px 12px",
      background: "rgba(255,255,255,0.03)",
      borderRadius: 10, border: "1px solid rgba(255,255,255,0.07)",
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
        color: "rgba(255,255,255,0.35)", marginBottom: 8 }}>PLAYTIME</div>

      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        {/* Online */}
        <div style={{ flex: 1, background: "rgba(68,255,170,0.06)", borderRadius: 8,
          padding: "8px 10px", border: "1px solid rgba(68,255,170,0.1)" }}>
          <div style={{ fontSize: 8, color: "#44ffaa99", fontWeight: 700,
            letterSpacing: 1, marginBottom: 3 }}>ONLINE</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#44ffaa" }}>
            {fmtDuration(online)}
          </div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", marginTop: 2 }}>
            {onlinePct}% of total
          </div>
        </div>

        {/* Offline */}
        <div style={{ flex: 1, background: "rgba(187,153,255,0.06)", borderRadius: 8,
          padding: "8px 10px", border: "1px solid rgba(187,153,255,0.1)" }}>
          <div style={{ fontSize: 8, color: "#bb99ff99", fontWeight: 700,
            letterSpacing: 1, marginBottom: 3 }}>OFFLINE</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#bb99ff" }}>
            {fmtDuration(offline)}
          </div>
          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", marginTop: 2 }}>
            {offlinePct}% of total
          </div>
        </div>
      </div>

      {/* Total + bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 5 }}>
        <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>Total</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.55)" }}>
          {fmtDuration(total)}
        </span>
      </div>
      {total > 0 && (
        <div style={{ height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${onlinePct}%`,
            background: "linear-gradient(90deg,#44ffaa,#00ccff)", borderRadius: 2 }} />
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function StatsTab({ effectiveStats, baseStats, xp, xpLevel, xpForLevel, ownedTiers, equippedTiers, shipPartBonuses, cardStatBonuses, onlinePlaytime, offlinePlaytime }) {
  const needed = xpForLevel(xpLevel);
  const pctXp  = needed > 0 ? Math.min(100, Math.round((xp / needed) * 100)) : 0;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px 24px",
      fontFamily: "'Share Tech Mono', monospace" }}>

      {/* ── Pilot level ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
        padding: "10px 12px", background: "rgba(255,255,255,0.03)",
        borderRadius: 10, border: "1px solid rgba(255,255,255,0.07)",
      }}>
        <div style={{ fontSize: 28, fontWeight: 900, color: "#bb99ff", lineHeight: 1, flexShrink: 0 }}>
          {xpLevel}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1,
            color: "rgba(187,153,255,0.6)", marginBottom: 4 }}>PILOT LEVEL</div>
          <div style={{ height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pctXp}%`,
              background: "linear-gradient(90deg,#7744ff,#bb99ff)", borderRadius: 3,
              transition: "width 0.4s ease" }} />
          </div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 3 }}>
            {fmt(xp)} / {fmt(needed)} XP
          </div>
        </div>
      </div>

      {/* ── Playtime ── */}
      <PlaytimeSection onlinePlaytime={onlinePlaytime} offlinePlaytime={offlinePlaytime} />

      {/* ── Source legend ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {[
          { label: "RESEARCH", color: "#44aaff" },
          { label: "SHIP",     color: "#ff9944" },
          { label: "ECHO",     color: "#bb77ff" },
        ].map(t => (
          <div key={t.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{
              fontSize: 6, fontWeight: 800, letterSpacing: 0.8,
              color: t.color, border: `1px solid ${t.color}55`,
              borderRadius: 3, padding: "1px 4px",
            }}>{t.label}</span>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>
              {t.label === "RESEARCH" ? "from research" : t.label === "SHIP" ? "from ship part" : "from rift echo"}
            </span>
          </div>
        ))}
      </div>

      {/* ── Ship parts section ── */}
      <ShipPartsSection ownedTiers={ownedTiers ?? {}} equippedTiers={equippedTiers ?? {}} />

      {/* ── Stat sections ── */}
      {SECTIONS.map(sec => (
        <div key={sec.label} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
            color: `${sec.color}99`, marginBottom: 5 }}>
            {sec.label}
          </div>
          <div style={{
            background: "rgba(255,255,255,0.02)", borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden",
          }}>
            {sec.rows.map((row, i) => {
              const raw = effectiveStats[row.key];
              if (raw == null) return null;
              const display  = row.fmt(raw);
              const defVal   = DEFAULT_STATS[row.key];
              const isDefault = defVal != null && display === row.fmt(defVal);
              const tags = getSourceTags(
                row.key, effectiveStats, baseStats,
                shipPartBonuses, cardStatBonuses,
              );
              return (
                <StatRow
                  key={row.key}
                  row={row}
                  value={display}
                  sectionColor={sec.color}
                  isDefault={isDefault}
                  isLast={i === sec.rows.length - 1}
                  sourceTags={tags}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
